import { Clickable, Col, Icon, Row, Text } from 'components';
import { theme } from 'lib';
import { CLAIM_LABELS, CLAIM_SUBTITLES, moveClaimInList } from './pollQuizPrecedence';

/**
 * The running order for the shared Team Channel slot, drawn as a tree.
 *
 * A tree because that is what the rule is: the server walks the list at the instant a fan opens the
 * channel and hands the slot to the first option that is currently available, falling through the
 * rest. Each step is therefore nested inside the one above it — the indentation IS the fallthrough.
 *
 * REORDERING ONLY. All four options are always listed and only their order can change. Removing an
 * option used to be possible, which made three other things necessary: buttons to add one back, a
 * dashed "Nothing" leaf for the case where the list was emptied, and a warning explaining what an
 * empty list would do. All of it is gone. The backend now rejects an empty order outright, and
 * `readOrder` completes any short list, so "which options" is settled and only "in what order" is a
 * question worth putting on screen.
 *
 * Two earlier attempts drew this as a horizontal match rail, which asserted a sequence in time that
 * does not exist: the four options are all evaluated at the same moment, and putting the quiz first
 * does not schedule it — it only means "prefer the quiz when there is a playable one".
 *
 * Purely controlled: holds no state, hands the whole next list to `onChange`.
 */

/**
 * Red and white, from the theme's own tokens.
 *
 * This replaces a two-colour scheme that tinted quiz rows green and poll rows blue, with local hex
 * constants because the theme had no dark green. That distinction is GONE: every node is now the same
 * red on white. Nothing is lost that the reader needs — each row already names its module in full
 * ("Quick Quiz — playable", "Poll — results"), so the colour was reinforcing the label rather than
 * carrying anything of its own.
 *
 * `theme.SECONDARY` is the accent, not `theme.PRIMARY` — the latter does not exist in theme.js at
 * all, despite 39 uses across the CMS, and every one of them silently falls back. That bug is what
 * made the Quiz Visibility tab render white text on no background.
 *
 * The label itself stays near-black rather than red. Red on white is about 3.5:1, which is under AA
 * for text this size; the border and the rank badge carry the colour instead, where a miss costs
 * nothing.
 */
const ACCENT = theme.SECONDARY;
const LABEL = theme.GREY_90;
/**
 * The subtitle sits a long way down the grey ramp from the label, which is what keeps a row scannable
 * now that it carries two lines: the labels alone still read as the list, and the conditions are there
 * for the admin who stops to ask why an option is not winning. GREY_60 rather than GREY_50 — the text
 * is small, and this is the last step on the ramp that still clears AA on white.
 */
const SUBTITLE = theme.GREY_60;
/**
 * Indent per level. One step per option, so the fourth sits three steps in — tightened from 26px
 * when this panel went from full width to half, to buy back room for the labels.
 */
const INDENT = 26;
const GAP = 9;

/**
 * The tree's connecting lines. A pale red rather than grey, so the structure belongs to the same
 * palette as the nodes, but well short of the accent — these lines are scaffolding and should not
 * compete with the rows they join.
 */
const rule = theme.SECONDARY_20;

const nodeBase = {
  flex: 1,
  minWidth: 0,
  padding: '12px 14px',
  borderRadius: '10px',
  // border: `1.5px solid ${ACCENT}`,
  backgroundColor: theme.PRIMARY_WHITE
};

const badgeBase = {
  width: '21px',
  height: '21px',
  flexShrink: 0,
  borderRadius: '6px',
  display: 'grid',
  placeItems: 'center'
};

/**
 * The elbow joining a node to its parent. An absolutely positioned box with two borders rather than
 * a `::before`, since these styles are inline.
 */
const Elbow = ({ depth }) => (
  <div
    style={{
      position: 'absolute',
      left: `${(depth - 1) * INDENT + 12}px`,
      top: `-${GAP}px`,
      width: '14px',
      height: `${GAP + 22}px`,
      borderLeft: `1.5px solid ${rule}`,
      borderBottom: `1.5px solid ${rule}`,
      borderBottomLeftRadius: '8px'
    }}
  />
);

/**
 * A move-up / move-down control.
 *
 * Third glyph, so worth recording what each one was. Bordered `Button`s carrying "↑" and "↓" came
 * first — that component is a 50px-radius pill with a 2px black border, right for a form's primary
 * action and far too loud for a control that appears eight times in one small tree and only nudges a
 * row by one place. Then `Icon type="arrowUp"`, a filled triangle occupying about a fifth of its
 * 24×24 viewBox: quiet enough, but so squat it read as a dot.
 *
 * Now `Icon type="up"` — the chevron the CMS uses in its own Dropdown. Its viewBox is 12×8 against a
 * square wrapper, so it stretches to fill the full height and stands far taller than the triangle
 * did. It also honours `col`, which the arrowUp/arrowDown pair does not (those hardcode black at 54%
 * opacity), so these can carry the row's own accent rather than sitting grey on a tinted card.
 *
 * `disabled` is guarded here as well as passed to `Clickable`, so the top row's up arrow cannot fire
 * even if that component's own handling changes.
 */
const Move = ({ type, title, col, disabled, onClick }) => (
  <Clickable disabled={disabled} onClick={() => !disabled && onClick()}>
    <div
      title={title}
      style={{
        width: '30px',
        height: '30px',
        display: 'grid',
        placeItems: 'center',
        opacity: disabled ? 0.3 : 1
      }}
    >
      <Icon type={type} dimensions={18} col={col} />
    </div>
  </Clickable>
);

const TreeRow = ({ depth, children }) => (
  <div style={{ position: 'relative', display: 'flex', paddingLeft: `${depth * INDENT}px` }}>
    {depth > 0 && <Elbow depth={depth} />}
    {children}
  </div>
);

const ClaimOrderEditor = ({ claims = [], onChange }) => {
  return (
    <Col gap={`${GAP}px`} wid="100%">
      {claims.map((claim, idx) => {
        return (
          <TreeRow key={claim} depth={idx}>
            <Row center gap="12px" style={nodeBase}>
              <Text
                c1
                bold
                col={theme.PRIMARY_WHITE}
                marg="0"
                style={{ ...badgeBase, backgroundColor: ACCENT, fontSize: '11.5px' }}
              >
                {idx + 1}
              </Text>

              <Col gap="3px" style={{ flex: 1, minWidth: 0 }}>
                <Text b2 bold col={LABEL} marg="0">
                  {CLAIM_LABELS[claim] ?? claim}
                </Text>
                {CLAIM_SUBTITLES[claim] && (
                  <Text c1 col={SUBTITLE} marg="0" style={{ lineHeight: 1.4 }}>
                    {CLAIM_SUBTITLES[claim]}
                  </Text>
                )}
              </Col>

              <Row center gap="2px" style={{ flexShrink: 0 }}>
                <Move
                  type="up"
                  col={ACCENT}
                  title="Check earlier"
                  disabled={idx === 0}
                  onClick={() => onChange(moveClaimInList(claims, claim, 'up'))}
                />
                <Move
                  type="down"
                  col={ACCENT}
                  title="Check later"
                  disabled={idx === claims.length - 1}
                  onClick={() => onChange(moveClaimInList(claims, claim, 'down'))}
                />
              </Row>
            </Row>
          </TreeRow>
        );
      })}
    </Col>
  );
};

export default ClaimOrderEditor;
